import { useBalance } from './hooks/use-balance';
import { useTradeSettings } from './hooks/use-trade-settings';
import { MarketResult } from './market-results';
import { useApmmRouterConfig } from './router/router-config';
import './style.css';
import { TradeConfirmEntry } from './trade-confirm-entry';
import { TreadInput } from './trade-input';
import { TreadOptions } from './trade-options';
import { TradeSettings } from './trade-settings';
import { shouldRefetchTradeQuery } from './trade-success-queries';
import { TreadSwitch } from './trade-switch';
import { useTradeLock } from './use-trade-lock';
import { Skeleton } from '@/client/components/ui/skeleton';
import { TERMS_OF_USE_LINK } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { TradeParams, useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useExpired } from '@/client/utils/clock';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { MarketStatus } from '@/libs/sdk/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TradePanelProps = {
  className?: string;
  tradeClassName?: string;
  params?: TradeParams;
  beforeOptions?: React.ReactNode;
  afterOptions?: React.ReactNode;
};

export const TradePanel = ({ className, tradeClassName, beforeOptions, afterOptions }: TradePanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useAuth();
  const uid = useMemo(() => data?.uid ?? '', [data?.uid]);
  const [successMessage, setSuccessMessage] = useState<string>();
  const { marketAddress, optionAddress, setOptionAddress, optionSide, setOptionSide, type, setType, amount, setAmount } =
    useTradeParamsStore(state => state);
  const market = useMarketDetail(api, { marketAddress });
  const isExpire = useExpired(Number(market.data?.deadline));
  const balance = useBalance({ type, marketAddress, optionAddress, uid });
  const { deadlineMinutes, slippage, isAutoSlippage } = useTradeSettings();
  // The router sets its own deadline, so the setting is hidden while the switch is on.
  const routerOn = useApmmRouterConfig().status === 'on';
  // While a router trade of this market runs, the side, the option and the amount cannot change under it.
  const locked = useTradeLock(marketAddress);
  const isInsufficientBalance = useMemo(
    () => amount !== undefined && balance.data !== undefined && Number(amount) > balance.data,
    [amount, balance.data]
  );
  const disabled = useMemo(
    () => !balance.data || isInsufficientBalance || balance.isLoading || isExpire,
    [balance.data, isInsufficientBalance, balance.isLoading, isExpire]
  );

  useEffect(() => {
    setSuccessMessage(undefined);
  }, [marketAddress, optionAddress, type]);

  const handleTradeSuccess = async () => {
    // Refetch the active trade queries only, and join a request already in flight.
    const refresh = () =>
      queryClient.invalidateQueries(
        { predicate: query => shouldRefetchTradeQuery(query.queryKey, { uid, marketAddress }) },
        { cancelRefetch: false }
      );
    await refresh();
    // SSE events refresh these queries after block-listener processing (~5s);
    // one fallback refresh covers a lost event.
    setTimeout(() => void refresh(), 6000);
    // A Router trade shows its own success box in the trade view; a second box here would repeat it.
    if (!routerOn) setSuccessMessage(type === 'buy' ? t('buy-success') : t('sell-success'));
  };

  if (market.isLoading || !market.data)
    return (
      <div className={cn('w-full space-y-3', className)}>
        <div
          className={cn('bg-card w-full space-y-6 md:rounded-6 md:border-.5 md:border-foreground/20 md:p-4', tradeClassName)}
        >
          <Skeleton className="h-10 w-full rounded-3" />
          <Skeleton className="h-14 w-full rounded-3" />
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-2" />
            <Skeleton className="h-10 w-full rounded-2" />
          </div>
          <Skeleton className="h-12 w-full rounded-3" />
          <Skeleton className="h-12 w-full rounded-3" />
        </div>
      </div>
    );
  if (market.data.marketStatus >= MarketStatus.settlement)
    return <MarketResult marketAddress={market.data.marketAddress} uid={uid} />;
  return (
    <div className={cn('w-full space-y-3', className)}>
      <div
        className={cn(
          'bg-card w-full space-y-4 md:rounded-6',
          'md:border-.5 md:border-foreground/20 md:p-4 md:space-y-6 md:shadow-[0px_7px_15px_0px] md:shadow-card/10',
          'transition-all duration-200 ease-in-out',
          type === 'buy' && 'min-h-[374px]',
          type === 'sell' && 'min-h-[382px]',
          tradeClassName
        )}
      >
        <TreadSwitch
          type={type}
          setType={type => {
            if (locked) return;
            setAmount(undefined);
            setType(type);
          }}
        />
        {beforeOptions}
        <TreadOptions
          type={type}
          market={market.data}
          optionAddress={optionAddress}
          setOptionAddress={locked ? undefined : setOptionAddress}
          optionSide={optionSide}
          setOptionSide={locked ? undefined : setOptionSide}
          uid={uid}
          onChainBalance={balance.data}
          onChainBalanceLoading={balance.isLoading}
        />
        {afterOptions}
        <div className="space-y-1">
          <div className="text-foreground/70 flex items-center justify-between">
            <span className="text-sm leading-3">{type == 'sell' ? t('shares') : t('amount')}</span>
            <TradeSettings hideDeadline={routerOn} />
          </div>
          <TreadInput
            disabled={isExpire || locked}
            type={type}
            maxAmount={balance.data}
            value={amount}
            onValueChange={setAmount}
            isLoading={balance.isLoading}
            decimal={balance.decimal}
            baseTokenAddress={market.data.baseTokenAddress}
          />
        </div>
        {isInsufficientBalance && (
          <div className="text-sm text-destructive px-1" role="alert">
            {t('insufficient-balance', '餘額不足')}
          </div>
        )}
        {isExpire && (
          <div className="text-center text-sm text-destructive mb-4">{t('market-closed-awaiting-resolution')}</div>
        )}
        {!isExpire && !optionAddress && amount != undefined && (
          <div className="text-center text-sm text-destructive mb-4">{t('please-choose-a-outcome')}</div>
        )}
        {successMessage && (
          <div className="rounded-3 border border-beneficial/30 bg-beneficial/10 px-4 py-3 text-sm font-medium text-beneficial">
            {successMessage}
          </div>
        )}
        <TradeConfirmEntry
          type={type}
          marketAddress={marketAddress}
          optionAddress={optionAddress}
          optionSide={optionSide}
          amount={amount}
          deadlineMinutes={deadlineMinutes}
          slippage={slippage}
          isAutoSlippage={isAutoSlippage}
          disabled={disabled}
          uid={uid}
          onSuccess={handleTradeSuccess}
        />
      </div>
      {/* copyright */}
      <p className="text-xs font-medium leading-3 text-center text-foreground/70">
        {t('by-trading-you-agree-to-the')}
        <a href={TERMS_OF_USE_LINK} target="_blank" className="underline ml-1">
          {t('terms-of-use')}
        </a>
      </p>
    </div>
  );
};
